/**
 * Starting automated survey assignments from the SPA: the manual "Start" path,
 * the page-load locked-queue check, and locks that come due mid-session
 * (router guard, idle timer, lockdown interceptor — ClickUp 868k2749y).
 * AutomatedSurveyChecker.vue re-exports these for existing importers.
 */
import get from 'lodash/get.js';

import { initialState } from '@/Helpers.js';
import AutomatedSurveyAssignment from '@/models/AutomatedSurveyAssignment.js';
import { trackAutomatedSurveyStarted } from '@/plugins/amplitude.js';
import axios from '@/plugins/axios.js';
import SnackbarService from '@/services/SnackbarService.js';
import EdverumToastService from '@/services/ToastService.js';
import store from '@/store/Index.js';
import {
    canEnforceLocks,
    claimLockNotice,
    fetchDueLockedAssignment,
    isRedirectingToLockedSurvey,
    isServerReportedDueLock,
    markRedirectingToLockedSurvey,
    recordLockStartFailure,
} from '@/utilities/automatedSurveyLockSchedule.js';

/**
 * Find the next assignment in queue from store, LoadingState, or API
 */
async function findNextAssignmentInQueue(nextAssignmentId) {
    try {
        const nextAssignment = AutomatedSurveyAssignment.find(nextAssignmentId);
        if (nextAssignment) {
            return nextAssignment;
        }

        // Check LoadingState assignments
        const loadedAssignments = initialState('AutomatedSurveyAssignments', []);
        const loadedAssignment = loadedAssignments.find(a => a.id === nextAssignmentId);
        if (loadedAssignment) {
            await AutomatedSurveyAssignment.insert({ data: [loadedAssignment] });
            return AutomatedSurveyAssignment.find(nextAssignmentId);
        }

        // Fetch from API as last resort
        const { data: nextData } = await axios.get(`/api/automated-survey-attempts/next-in-queue`);
        if (nextData?.data?.id === nextAssignmentId) {
            await AutomatedSurveyAssignment.insert({ data: [nextData.data] });
            return AutomatedSurveyAssignment.find(nextAssignmentId);
        }
    } catch (error) {
        console.error('Failed to find next assignment in queue:', error);
    }
    return null;
}

/**
 * The start was refused because another attempt is still open
 * (`otherAutomatedSurveyActive`, HTTP 400). Send the learner to that attempt so
 * they can finish it; the locked survey can start only after it ends.
 *
 * An evaluation attempt carries `other_attempt.automated_survey_assignment`. A quiz
 * or activity survey attempt does not (it is null), so it is routed through the
 * `meta.active_attempt` deep link to its activity instead (ClickUp 868k2749y).
 *
 * @param {unknown} error
 * @returns {{route: Object, message: string, assignment: Object|null}|null}
 */
export function routeForOtherActiveAttempt(error) {
    const meta = get(error, 'response.data.meta') || {};
    const otherAttempt = meta.other_attempt;

    if (!otherAttempt) {
        return null;
    }

    const assignment = otherAttempt.automated_survey_assignment;

    if (assignment?.id && assignment?.survey_id) {
        return {
            route: {
                name: 'automated-survey-attempt',
                params: {
                    id: assignment.survey_id,
                    assignmentId: assignment.id,
                    attemptId: otherAttempt.id,
                },
            },
            message: 'You already have an active survey attempt. Redirecting...',
            assignment,
        };
    }

    const activeAttempt = meta.active_attempt;

    if (activeAttempt?.activity_id && activeAttempt?.course_id) {
        const title = activeAttempt.title ? `"${activeAttempt.title}"` : 'your current activity';

        return {
            route: {
                name: 'activity-view',
                params: { courseId: activeAttempt.course_id, activityId: activeAttempt.activity_id },
            },
            message: `Finish ${title} first. Your required evaluation will start after it.`,
            assignment: null,
        };
    }

    return null;
}

/**
 * @param {Object} surveyAssignment
 * @param {number} [retryCount]
 * @param {Object} [options]
 * @param {boolean} [options.quiet] Automatic (not user-initiated) start: skip the
 *   queue-correction retry and its toasts; the caller reports one failure itself.
 */
export const routeToAutomatedSurveyAssignment = async (surveyAssignment, retryCount = 0, { quiet = false } = {}) => {
    // Prevent infinite recursion
    if (retryCount > 1) {
        throw new Error('Unable to start survey. Please refresh the page and try again.');
    }

    try {
        const { data } = await axios.post(
            '/api/automated-survey-attempts',
            { automated_survey_assignment_id: surveyAssignment.id },
            { errorHandle: false }
        );

        // Track survey start for Amplitude Targeted Replay Capture
        await trackAutomatedSurveyStarted(surveyAssignment);

        return {
            name: 'automated-survey-attempt',
            params: {
                id: surveyAssignment.survey_id,
                assignmentId: surveyAssignment.id,
                attemptId: data.data.id,
            },
        };
    } catch (e) {
        const otherAttemptRoute = routeForOtherActiveAttempt(e);

        if (otherAttemptRoute) {
            SnackbarService.open({
                message: otherAttemptRoute.message,
                type: 'is-danger',
                duration: 5000,
            });

            if (otherAttemptRoute.assignment) {
                // Track survey start for Amplitude Targeted Replay Capture (resumed attempt)
                await trackAutomatedSurveyStarted(otherAttemptRoute.assignment);
            }

            return otherAttemptRoute.route;
        }

        if (quiet) {
            throw e;
        }

        // Handle queue order validation error (422)
        // Note: This only happens when a user manually clicks "Start Evaluation" on an assignment
        // that's not first in queue. We guide them to the correct one, but don't force auto-start.
        // Auto-start (forcing) only happens via AutomatedSurveyChecker.startLockedAutomatedSurveys()
        // which only acts on assignments where locked_at is in the past.
        if (e.response?.status === 422 && e.response?.data?.next_assignment_id) {
            const nextAssignmentId = e.response.data.next_assignment_id;
            const errorMessage =
                e.response.data.message || 'This survey is no longer the next in queue. Please refresh the page.';

            // Show error and guide user to correct assignment
            EdverumToastService.open({
                message: `${errorMessage} Starting the correct survey instead.`,
                type: 'is-warning',
                duration: 8000,
            });

            // Try to find and start the correct assignment
            // This is still a manual action (user clicked "Start Evaluation"), just correcting to the right one
            const nextAssignment = await findNextAssignmentInQueue(nextAssignmentId);
            if (nextAssignment) {
                // Recursively call with the correct assignment (with retry count)
                return routeToAutomatedSurveyAssignment(nextAssignment, retryCount + 1);
            }

            // Queue toast already shown; avoid a second toast below.
            throw e;
        }

        // Remaining 422s (already-started, unavailable, expired, etc.) are silenced by
        // handleResponseError's global 422 snackbar suppression — surface them here so
        // Start Evaluation does not look like a dead button (ClickUp 868kuch4z).
        if (e.response?.status === 422) {
            EdverumToastService.open({
                message:
                    e.response.data?.message ||
                    'Unable to start this evaluation. Please refresh the page and try again.',
                type: 'is-danger',
                duration: 8000,
            });
        }

        throw e;
    }
};

/**
 * Create survey attempt with retry logic for network failures
 */
export async function createSurveyAttemptWithRetry(assignment, maxRetries = 3, options = {}) {
    // Helper function to create a delay
    const delay = ms =>
        new Promise(resolve => {
            setTimeout(resolve, ms);
        });

    // Recursive retry function to avoid await in loop
    const attemptRequest = async attempt => {
        try {
            // Use the existing function with error handling
            const route = await routeToAutomatedSurveyAssignment(assignment, 0, options);
            return route;
        } catch (error) {
            console.warn(`[DEBUG-AutomatedSurveyChecker] Survey creation attempt ${attempt} failed:`, error.message);

            // Don't retry on validation errors (4xx), only network/server errors (5xx)
            if (error.response?.status >= 400 && error.response?.status < 500) {
                console.error('[DEBUG-AutomatedSurveyChecker] Client error detected, not retrying');
                throw error;
            }

            // If we haven't exceeded max retries, wait and try again
            if (attempt < maxRetries) {
                const delayMs = 2 ** (attempt - 1) * 1000; // 1s, 2s, 4s
                console.warn(`[DEBUG-AutomatedSurveyChecker] Waiting ${delayMs}ms before retry...`);
                await delay(delayMs);
                return attemptRequest(attempt + 1);
            }

            // All attempts failed
            console.error(
                `[DEBUG-AutomatedSurveyChecker] All ${maxRetries} attempts failed for assignment ${assignment.id}`
            );
            throw error;
        }
    };

    return attemptRequest(1);
}

/**
 * Start (or resume) a locked assignment and full-page redirect into it.
 * Shared by the page-load check and every mid-session lock trigger.
 *
 * @param {Object} assignment
 * @param {import('vue-router').Router} router
 * @param {Object} [options] Passed to routeToAutomatedSurveyAssignment.
 */
export async function redirectIntoLockedAssignment(assignment, router, options = {}) {
    const assignmentRoute = await createSurveyAttemptWithRetry(assignment, 3, options);
    const resolved = router.resolve(assignmentRoute);

    // The learner is already on the attempt they must finish first (another open
    // attempt refused the start): stay put rather than reload the same page forever.
    if (resolved.path === window.location.pathname) {
        return;
    }

    // Open a toast message based on the assignment type
    const message = assignment.course_id ? 'Starting evaluation...' : 'Starting survey...';
    SnackbarService.open({ message });

    // Dismiss any notifications related to automated surveys and set redirect info
    await store.dispatch('AutomatedSurveys/dismissNotification');
    store.commit('AutomatedSurveys/SET_REDIRECT', router.resolve({ name: 'home' }));

    markRedirectingToLockedSurvey();

    // Redirect to the calculated route
    window.location.href = resolved.href;
}

const DUE_LOCK_BLOCKED_MESSAGE = 'A required survey must be completed before you can continue.';
const DUE_LOCK_START_FAILED_MESSAGE =
    'Your required evaluation could not be started. Please refresh the page or contact support if this continues.';

/** Longest the navigation waits for in-flight saves before moving into the survey. */
export const PENDING_SAVES_TIMEOUT_MS = 5000;

/**
 * Let saves the current page already started finish before the full-page redirect,
 * so a lock never costs the learner work they were saving. Uses the shared AutoSave
 * state (`AutoSave/allSaved`); bounded so a stuck save cannot block the survey.
 *
 * @param {number} [timeoutMs]
 * @returns {Promise<void>}
 */
export async function waitForPendingSaves(timeoutMs = PENDING_SAVES_TIMEOUT_MS) {
    const allSaved = () => store.getters?.['AutoSave/allSaved'] !== false;
    const deadline = Date.now() + timeoutMs;

    while (!allSaved() && Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => {
            setTimeout(resolve, 100);
        });
    }
}

/**
 * Tell the learner, once per notice window, why an action was refused.
 *
 * @param {string} [message] Server message from the `automated_survey_locked` response.
 */
export function notifyBlockedByDueLock(message) {
    if (!claimLockNotice()) {
        return;
    }

    EdverumToastService.open({
        message: message || DUE_LOCK_BLOCKED_MESSAGE,
        type: 'is-warning',
        duration: 8000,
    });
}

function isNotStartable(error) {
    const status = error?.response?.status;

    return status >= 400 && status < 500;
}

let dueLockStart = null;

/**
 * On the learner's next navigation, move them into a locked assignment that came
 * due after the page loaded. Single-flight and throttled. The server's queue
 * decides which assignment (if any) to start; in-flight saves finish first. A
 * failed start backs off exponentially, drops locks the server refused to start,
 * and shows at most one toast.
 *
 * @param {import('vue-router').Router} router
 * @param {Object} [options]
 * @param {boolean} [options.force] Skip the empty-answer throttle (defaults to
 *   whether the server already reported a due lock).
 * @returns {Promise<boolean>} Whether the page is redirecting into a survey.
 */
export function startDueLockedAutomatedSurvey(router, { force = isServerReportedDueLock() } = {}) {
    if (isRedirectingToLockedSurvey()) {
        return Promise.resolve(true);
    }

    if (!canEnforceLocks()) {
        return Promise.resolve(false);
    }

    if (!dueLockStart) {
        dueLockStart = (async () => {
            try {
                const assignment = await fetchDueLockedAssignment({ force });

                if (!assignment) {
                    return false;
                }

                await waitForPendingSaves();
                await redirectIntoLockedAssignment(assignment, router, { quiet: true });

                return true;
            } catch (error) {
                const failures = recordLockStartFailure({ notStartable: isNotStartable(error) });

                console.error('[AutomatedSurveyChecker] Unable to start a locked survey that came due:', error);

                if (failures === 1 && claimLockNotice()) {
                    EdverumToastService.open({
                        message: DUE_LOCK_START_FAILED_MESSAGE,
                        type: 'is-danger',
                        duration: 10000,
                    });
                }

                return false;
            } finally {
                dueLockStart = null;
            }
        })();
    }

    return dueLockStart;
}
